// @flow

/**
 * Module dependencies.
 */

import { ErrorMessage } from 'client/components/forms/components/messages';
import { Input } from 'client/components/core/forms';
import { LoadingButton } from 'client/components/core/buttons';
import { colors, units } from 'client/styles';
import { compose, defaultProps } from 'recompose';
import { isEmail } from 'client/core/utils';
import { translator } from 'core/utils';
import FormContainer from 'client/components/forms/components/form-container';
import Layout from 'client/components/core/layout';
import Loader from 'client/components/forms/components/loader';
import Panel from 'client/components/forms/components/panel';
import React, { Component } from 'react';
import Text from 'client/components/core/type';
import formSubmitWrapper from 'client/components/forms/components/form-submit-wrapper';

/**
 * `SuccessContent` type.
 */

type SuccessContent = {
  button?: string,
  buttonThemeColor?: string,
  message?: string,
  title?: string
};

/**
 * `Props` type.
 */

type Props = {
  autofocus?: boolean,
  fluid: boolean,
  fullfilled: boolean,
  onReset: Function,
  onSubmit: Function,
  pending: boolean,
  reason: string,
  submissionError: boolean,
  submitLabel: string,
  successContent: SuccessContent,
  title: string,
  translate: Function
};

/**
 * `FormContact` component.
 */

class FormContact extends Component {

  /**
   * Props.
   */

  props: Props;

  /**
   * State.
   */

  state: Object = {
    email: '',
    reason: ''
  };

  /**
   * Reset form fields.
   */

  resetForm = (event: any) => {
    event.preventDefault();

    this.setInitialState();
  }

  /**
   * Is form invalid.
   */

  isFormInvalid = (): boolean => {
    const { email } = this.state;

    return !email.length || !isEmail(email);
  }

  /**
   * Set initial state.
   */

  setInitialState = () => {
    const { onReset } = this.props;

    this.setState({
      email: '',
      reason: this.props.reason
    }, onReset);
  }

  /**
   * Handle input change.
   */

  handleInputChange = ({ target }: any) => {
    const { value, name } = target;

    this.setState({
      [name]: value
    });
  }

  /**
   * Handle form submit.
   */

  handleSubmit = (event: any) => {
    event.preventDefault();

    if (this.isFormInvalid()) {
      return;
    }

    this.props.onSubmit(this.state);
  };

  /**
   * Component will mount.
   */

  componentWillMount() {
    this.setInitialState();
  }

  /**
   * Render
   */

  render(): React$Element<*> {
    const {
      autofocus,
      fluid,
      fullfilled,
      pending,
      reason,
      submissionError,
      submitLabel,
      successContent,
      title,
      translate
    } = this.props;

    const emailAutoFocusId = autofocus && 'email';
    const themeColor = colors.brandSecondary;

    return (
      <Panel
        fluid={fluid}
        themeColor={themeColor}
      >
        <Loader
          isLoading={pending}
          themeColor={themeColor}
        />

        <FormContainer
          content={successContent}
          formVisible={!pending && !fullfilled}
          resetForm={this.resetForm}
          successVisible={fullfilled}
        >
          <Text.H4 color={colors.shadesOfBlue.bigStoneDarker}>
            {title}
          </Text.H4>

          <form onSubmit={this.handleSubmit}>
            <Layout marginBottom={units(3)}>
              <Input
                autofocus
                id={emailAutoFocusId}
                label={translate('formRequest.labelEmail')}
                name={'email'}
                onUpdate={this.handleInputChange}
                required
                themeColor={themeColor}
                validation={isEmail}
                value={this.state.email}
              />
            </Layout>

            <Layout marginBottom={units(2.5)}>
              <LoadingButton
                disabled={this.isFormInvalid()}
                element={'button'}
                loading={pending}
                themeColor={themeColor}
                type={'submit'}
              >
                {submitLabel}
              </LoadingButton>
            </Layout>

            <ErrorMessage
              message={translate('formRequest.message.error')}
              visible={submissionError}
            />

            <input
              name={'reason'}
              type={'hidden'}
              value={reason}
            />
          </form>
        </FormContainer>
      </Panel>
    );
  }

}

/**
 * Export `FormContact`.
 */

export default compose(
  defaultProps({
    autofocus: false,
    fluid: false
  }),
  translator()
)(formSubmitWrapper(FormContact));



// WEBPACK FOOTER //
// ../src/client/components/forms/form-contact.js